import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Activity, X, RefreshCw } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import OrderModal from '../components/OrderModal';
import { formatPrice } from '../utils/formatters';
import {
  SCANNER_INFO, SCANNER_CATEGORY_MAP, SCANNER_CATEGORY_LABELS, PATTERN_INFO,
} from './scanners/scannerConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const UNIVERSE_OPTIONS = [
  { id: 'nifty50', label: 'Nifty 50' },
  { id: 'all',     label: 'All NSE'  },
];
const PRICE_RANGES = [
  { id: '<500',      label: 'Under ₹500' },
  { id: '500-1000',  label: '₹500–₹1k'  },
  { id: '1000-3000', label: '₹1k–₹3k'   },
  { id: '>3000',     label: 'Above ₹3k' },
];
const SORT_OPTS = [
  { id: 'name',   label: 'Name'  },
  { id: 'price',  label: 'Price' },
  { id: 'change', label: 'Chg%'  },
  { id: 'mcap',   label: 'MCap'  },
];

const NIFTY50_SYMBOLS = new Set([
  'ADANIENT','ADANIPORTS','APOLLOHOSP','ASIANPAINT','AXISBANK','BAJAJ-AUTO',
  'BAJFINANCE','BAJAJFINSV','BPCL','BHARTIARTL','BRITANNIA','CIPLA','COALINDIA',
  'DIVISLAB','DRREDDY','EICHERMOT','ETERNAL','GRASIM','HCLTECH','HDFCBANK',
  'HDFCLIFE','HEROMOTOCO','HINDALCO','HINDUNILVR','ICICIBANK','INDUSINDBK',
  'INFY','ITC','JSWSTEEL','KOTAKBANK','LT','M&M','MARUTI','NESTLEIND','NTPC',
  'ONGC','POWERGRID','RELIANCE','SBILIFE','SBIN','SUNPHARMA','TATACONSUM',
  'TATAMOTORS','TATASTEEL','TCS','TECHM','TITAN','TRENT','ULTRACEMCO','WIPRO',
]);

function Pill({ active, onClick, children }) {
  const { colors } = useTheme();
  return (
    <button onClick={onClick}
      className="flex-shrink-0 px-2.5 py-1 rounded-full text-[12px] font-semibold transition-all"
      style={{
        backgroundColor: active ? colors.accent : colors.inputBg,
        color: active ? '#fff' : colors.secondaryText,
        border: `1px solid ${active ? colors.accent : colors.border}`,
      }}>
      {children}
    </button>
  );
}

export default function ScreenerPage() {
  const { colors } = useTheme();
  const navigate   = useNavigate();
  const techRef    = useRef(null);
  const patternRef = useRef(null);

  const [stocks,         setStocks]         = useState([]);
  const [scanResults,    setScanResults]    = useState([]);
  const [patterns,       setPatterns]       = useState([]);
  const [loading,        setLoading]        = useState(true);

  const [universe,       setUniverse]       = useState('nifty50');
  const [timeframe,      setTimeframe]      = useState('swing');
  const [search,         setSearch]         = useState('');
  const [sector,         setSector]         = useState(null);
  const [priceRange,     setPriceRange]     = useState(null);
  const [sortBy,         setSortBy]         = useState('name');
  const [sortDir,        setSortDir]        = useState('asc');
  const [techFilters,    setTechFilters]    = useState(new Set());
  const [patternFilters, setPatternFilters] = useState(new Set());
  const [techOpen,       setTechOpen]       = useState(false);
  const [patternOpen,    setPatternOpen]    = useState(false);

  const [orderModalOpen,  setOrderModalOpen]  = useState(false);
  const [orderModalStock, setOrderModalStock] = useState(null);

  const norm = s => (s || '').replace('.NS', '').toUpperCase();

  const fetchEquity = async (tf) => {
    setLoading(true);
    try {
      const [stocksRes, scanRes, patternRes] = await Promise.all([
        stocks.length ? null : axios.get(`${API_URL}/stocks`),
        axios.get(`${API_URL}/scanners/results`, { params: { limit: 500, timeframe: tf } }),
        tf === 'swing'
          ? axios.get(`${API_URL}/patterns`)
          : axios.get(`${API_URL}/patterns/intraday`, { params: { timeframe: tf } }),
      ]);
      if (stocksRes) setStocks(stocksRes.data || []);
      setScanResults(scanRes.data || []);
      setPatterns(patternRes.data?.patterns || []);
    } catch (_) {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEquity(timeframe); }, []); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if (techRef.current && !techRef.current.contains(e.target)) setTechOpen(false);
      if (patternRef.current && !patternRef.current.contains(e.target)) setPatternOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Filter + sort ──────────────────────────────────────────────────────────
  const techSets = {};
  techFilters.forEach(name => {
    techSets[name] = new Set(scanResults.filter(r => r.scanner_name === name).map(r => norm(r.symbol)));
  });
  const patternSets = {};
  patternFilters.forEach(pt => {
    patternSets[pt] = new Set(patterns.filter(p => p.pattern_type === pt).map(p => norm(p.symbol)));
  });

  let results = stocks.filter(s => {
    const sym   = norm(s.symbol);
    const price = s.current_price;
    if (universe === 'nifty50' && !NIFTY50_SYMBOLS.has(sym)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!s.symbol.toLowerCase().includes(q) && !(s.company_name || '').toLowerCase().includes(q)) return false;
    }
    if (sector && s.sector !== sector) return false;
    if (priceRange && price) {
      if (priceRange === '<500'      && price >= 500)               return false;
      if (priceRange === '500-1000'  && (price < 500  || price >= 1000)) return false;
      if (priceRange === '1000-3000' && (price < 1000 || price >= 3000)) return false;
      if (priceRange === '>3000'     && price < 3000)               return false;
    }
    if (techFilters.size > 0    && ![...techFilters].every(n => techSets[n].has(sym)))    return false;
    if (patternFilters.size > 0 && ![...patternFilters].every(p => patternSets[p].has(sym))) return false;
    return true;
  });

  results.sort((a, b) => {
    let av, bv;
    if      (sortBy === 'price')  { av = a.current_price || 0; bv = b.current_price || 0; }
    else if (sortBy === 'change') { av = a.change_pct    || 0; bv = b.change_pct    || 0; }
    else if (sortBy === 'mcap')   { av = a.market_cap    || 0; bv = b.market_cap    || 0; }
    else                          { av = (a.company_name || '').toLowerCase(); bv = (b.company_name || '').toLowerCase(); }
    if (av === bv) return 0;
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const allSectors       = [...new Set(stocks.map(s => s.sector).filter(Boolean))].sort();
  const universeTotal    = stocks.filter(s => universe === 'nifty50' ? NIFTY50_SYMBOLS.has(norm(s.symbol)) : true).length;
  const activeFilterCount = (sector ? 1 : 0) + (priceRange ? 1 : 0) + techFilters.size + patternFilters.size;

  const clearAll = () => {
    setSector(null); setPriceRange(null);
    setTechFilters(new Set()); setPatternFilters(new Set()); setSearch('');
  };

  const openOrder = (s) => {
    setOrderModalStock({ symbol: norm(s.symbol), company_name: s.company_name, current_price: s.current_price });
    setOrderModalOpen(true);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-3 pb-8">

      {/* Header */}
      <div className="px-1">
        <h1 className="text-[18px] font-black" style={{ color: colors.primaryText }}>Screener</h1>
        <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
          Filter and scan equities · all data scoped to sim date
        </p>
      </div>

      <div className="space-y-2">

        {/* Row 1: Universe + Timeframe + Refresh */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {UNIVERSE_OPTIONS.map(u => (
            <Pill key={u.id} active={universe === u.id} onClick={() => setUniverse(u.id)}>{u.label}</Pill>
          ))}
          <div className="w-px h-4 mx-1" style={{ backgroundColor: colors.border }} />
          {['swing', '5min', '15min'].map(tf => (
            <Pill key={tf} active={timeframe === tf}
              onClick={() => { setTimeframe(tf); setTechFilters(new Set()); setPatternFilters(new Set()); fetchEquity(tf); }}>
              {tf === 'swing' ? 'Swing' : tf}
            </Pill>
          ))}
          <button onClick={() => fetchEquity(timeframe)}
            className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full text-[12px]"
            style={{ color: colors.secondaryText, border: `1px solid ${colors.border}` }}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* Row 2: Search + Sector + Price */}
        <div className="flex gap-1.5 items-center">
          <input
            type="text" placeholder="Search symbol or company…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 min-w-0 px-3 py-1.5 rounded-lg text-[12px] outline-none"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
          />
          <select value={sector || ''} onChange={e => setSector(e.target.value || null)}
            className="px-2 py-1.5 rounded-lg text-[12px] outline-none flex-shrink-0"
            style={{ backgroundColor: colors.inputBg, color: sector ? colors.accent : colors.secondaryText, border: `1px solid ${sector ? colors.accent : colors.border}` }}>
            <option value="">All Sectors</option>
            {allSectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={priceRange || ''} onChange={e => setPriceRange(e.target.value || null)}
            className="px-2 py-1.5 rounded-lg text-[12px] outline-none flex-shrink-0"
            style={{ backgroundColor: colors.inputBg, color: priceRange ? colors.accent : colors.secondaryText, border: `1px solid ${priceRange ? colors.accent : colors.border}` }}>
            <option value="">All Prices</option>
            {PRICE_RANGES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>

        {/* Row 3: Sort + Scanner + Pattern + Clear + Count */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {SORT_OPTS.map(({ id, label }) => (
            <Pill key={id} active={sortBy === id} onClick={() => setSortBy(id)}>{label}</Pill>
          ))}
          <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="px-2 py-1 rounded-full text-[12px] font-bold flex-shrink-0"
            style={{ backgroundColor: colors.inputBg, color: colors.accent, border: `1px solid ${colors.border}` }}>
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>

          <div className="w-px h-4 mx-0.5" style={{ backgroundColor: colors.border }} />

          {/* Scanner dropdown */}
          <div className="relative" ref={techRef}>
            <button onClick={() => { setTechOpen(o => !o); setPatternOpen(false); }}
              className="flex items-center gap-0.5 px-2.5 py-1 rounded-full text-[12px] font-medium"
              style={{
                backgroundColor: techFilters.size > 0 ? `${colors.accent}20` : colors.inputBg,
                color: techFilters.size > 0 ? colors.accent : colors.secondaryText,
                border: `1px solid ${techFilters.size > 0 ? colors.accent : colors.border}`,
              }}>
              + Scanner
              {techFilters.size > 0 && <span className="ml-1 px-1.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: colors.accent, color: '#fff' }}>{techFilters.size}</span>}
              <ChevronRight className="w-3 h-3" style={{ transform: techOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {techOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 w-72 rounded-xl shadow-xl overflow-y-auto"
                style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, maxHeight: '300px' }}>
                {Object.entries(SCANNER_CATEGORY_LABELS).map(([catKey, catMeta]) => {
                  const scanners = Object.entries(SCANNER_INFO).filter(([n]) => SCANNER_CATEGORY_MAP[n] === catKey);
                  if (!scanners.length) return null;
                  return (
                    <div key={catKey}>
                      <p className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide sticky top-0"
                        style={{ backgroundColor: colors.cardBg, color: colors.accent }}>{catMeta.label}</p>
                      {scanners.map(([name, info]) => {
                        const sel = techFilters.has(name);
                        const dc  = info.direction === 'bullish' ? '#16C784' : info.direction === 'bearish' ? '#ef4444' : colors.accent;
                        return (
                          <button key={name}
                            onClick={() => { const n = new Set(techFilters); sel ? n.delete(name) : n.add(name); setTechFilters(n); }}
                            className="w-full flex items-center justify-between px-3 py-1.5 text-left text-[12px]"
                            style={{ backgroundColor: sel ? `${dc}15` : 'transparent', color: colors.primaryText }}>
                            <span>{info.label}</span>
                            {sel && <span style={{ color: dc }}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pattern dropdown */}
          <div className="relative" ref={patternRef}>
            <button onClick={() => { setPatternOpen(o => !o); setTechOpen(false); }}
              className="flex items-center gap-0.5 px-2.5 py-1 rounded-full text-[12px] font-medium"
              style={{
                backgroundColor: patternFilters.size > 0 ? `${colors.accent}20` : colors.inputBg,
                color: patternFilters.size > 0 ? colors.accent : colors.secondaryText,
                border: `1px solid ${patternFilters.size > 0 ? colors.accent : colors.border}`,
              }}>
              + Pattern
              {patternFilters.size > 0 && <span className="ml-1 px-1.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: colors.accent, color: '#fff' }}>{patternFilters.size}</span>}
              <ChevronRight className="w-3 h-3" style={{ transform: patternOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {patternOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 w-56 rounded-xl shadow-xl overflow-y-auto"
                style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, maxHeight: '260px' }}>
                {Object.entries(PATTERN_INFO).map(([ptype, info]) => {
                  const sel = patternFilters.has(ptype);
                  return (
                    <button key={ptype}
                      onClick={() => { const n = new Set(patternFilters); sel ? n.delete(ptype) : n.add(ptype); setPatternFilters(n); }}
                      className="w-full flex items-center justify-between px-3 py-1.5 text-left text-[12px]"
                      style={{ backgroundColor: sel ? `${info.color}15` : 'transparent', color: colors.primaryText }}>
                      <span>{info.label}</span>
                      <span className="text-[11px] ml-2" style={{ color: info.color }}>{info.description}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {activeFilterCount > 0 && (
            <button onClick={clearAll}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px]"
              style={{ backgroundColor: colors.inputBg, color: colors.secondaryText, border: `1px solid ${colors.border}` }}>
              <X className="w-3 h-3" /> Clear
            </button>
          )}

          <span className="ml-auto text-[12px] flex-shrink-0" style={{ color: colors.secondaryText }}>
            {loading ? '…' : `${results.length}/${universeTotal}`}
          </span>
        </div>

        {/* Active filter chips */}
        {(techFilters.size > 0 || patternFilters.size > 0) && (
          <div className="flex flex-wrap gap-1.5">
            {[...techFilters].map(name => {
              const info = SCANNER_INFO[name];
              const dc   = info?.direction === 'bullish' ? '#16C784' : info?.direction === 'bearish' ? '#ef4444' : colors.accent;
              return (
                <span key={name} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                  style={{ backgroundColor: `${dc}20`, color: dc, border: `1px solid ${dc}40` }}>
                  {info?.label || name}
                  <button onClick={() => { const n = new Set(techFilters); n.delete(name); setTechFilters(n); }}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              );
            })}
            {[...patternFilters].map(ptype => {
              const info = PATTERN_INFO[ptype];
              return (
                <span key={ptype} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                  style={{ backgroundColor: `${info?.color || colors.accent}20`, color: info?.color || colors.accent, border: `1px solid ${(info?.color || colors.accent)}40` }}>
                  {info?.label || ptype}
                  <button onClick={() => { const n = new Set(patternFilters); n.delete(ptype); setPatternFilters(n); }}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Results table */}
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: colors.accent, borderTopColor: 'transparent' }} />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-10">
              <Activity className="w-8 h-8 mx-auto mb-2" style={{ color: colors.secondaryText }} />
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>No stocks match your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div style={{ minWidth: '460px' }}>
                {/* Header */}
                <div className="grid px-3 py-2 text-[11px] font-bold uppercase tracking-wide"
                  style={{
                    gridTemplateColumns: '80px 1fr 100px 72px 56px 60px',
                    gap: '8px',
                    borderBottom: `1px solid ${colors.border}`,
                    color: colors.secondaryText,
                    backgroundColor: colors.cardBg,
                  }}>
                  <span>Symbol</span>
                  <span>Company</span>
                  <span>Sector</span>
                  <span className="text-right">Price</span>
                  <span className="text-right">Chg%</span>
                  <span />
                </div>

                {/* Rows */}
                {results.map((s, idx) => {
                  const price  = s.current_price;
                  const chgPct = s.change_pct;
                  const chgUp  = chgPct >= 0;
                  const sym    = norm(s.symbol);
                  const matched = [
                    ...[...techFilters].map(n => SCANNER_INFO[n]?.label || n),
                    ...[...patternFilters].map(p => PATTERN_INFO[p]?.label || p),
                  ];

                  return (
                    <div key={idx}
                      className="grid px-3 py-2"
                      style={{
                        gridTemplateColumns: '80px 1fr 100px 72px 56px 60px',
                        gap: '8px',
                        borderBottom: idx < results.length - 1 ? `1px solid ${colors.border}` : 'none',
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = `${colors.accent}08`}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>

                      <button className="text-left font-bold text-[12px] self-center"
                        style={{ color: colors.accent }}
                        onClick={() => navigate(`/markets?symbol=${sym}`)}>
                        {sym}
                      </button>

                      <div className="min-w-0 self-center">
                        <p className="text-[12px] truncate" style={{ color: colors.primaryText }}>{s.company_name}</p>
                        {matched.length > 0 && (
                          <p className="text-[11px] truncate mt-0.5" style={{ color: colors.accent }}>{matched.join(' · ')}</p>
                        )}
                      </div>

                      <span className="text-[11px] self-center truncate" style={{ color: colors.secondaryText }}>
                        {s.sector || '—'}
                      </span>

                      <span className="text-[12px] text-right self-center font-medium" style={{ color: colors.primaryText }}>
                        {price ? formatPrice(price, 0) : '—'}
                      </span>

                      <span className="text-[12px] text-right self-center font-semibold"
                        style={{ color: chgPct != null ? (chgUp ? '#16C784' : '#ef4444') : colors.secondaryText }}>
                        {chgPct != null ? `${chgUp ? '+' : ''}${chgPct.toFixed(1)}%` : '—'}
                      </span>

                      <div className="flex items-center justify-end self-center">
                        <button onClick={() => openOrder(s)}
                          className="px-2 py-0.5 rounded-md text-[11px] font-bold"
                          style={{ backgroundColor: '#16C78420', color: '#16C784', border: '1px solid #16C78440' }}>
                          Trade
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {orderModalOpen && orderModalStock && (
        <OrderModal
          isOpen={orderModalOpen}
          onClose={() => setOrderModalOpen(false)}
          stock={orderModalStock}
          side="BUY"
        />
      )}
    </div>
  );
}
